import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6 },
  }),
};

function About() {
  return (
    <>
      <div id="rapid-about-us" className="page"></div>
      <Header />
      {/* ================= ABOUT SECTION ================= */}
      <section
        style={{ scrollMarginTop: "80px" }}
        className="bg-light py-5 border my-shadow"
      >
        <div className="container">
          {/* TRUST / ACCREDITATION ROW */}
          <motion.div
            className="row text-center mb-5 g-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              "Ghana Health Service Licensed",
              "HeFRA Registered Facility",
              "Certified Diagnostic Standards",
              "Quality Controlled Laboratory",
            ].map((item, i) => (
              <motion.div
                key={i}
                className="col-6 col-md-3"
                custom={i}
                variants={fadeUp}
              >
                <div className="bg-white p-3 rounded shadow-sm h-100 border hover-card">
                  <div className="fw-bold text-primary-emphasis small">
                    {item}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="row align-items-center g-5">
            {/* IMAGE */}
            <motion.div
              className="col-lg-6 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                className="img-fluid rounded shadow"
                src="images/assets/rapid-Lab-staff.png"
                alt="Rapid Lab Diagnostics Team"
              />
            </motion.div>

            {/* CONTENT CARD */}
            <div className="col-lg-6">
              <motion.div
                className="card border-0 shadow-sm p-4"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="mb-3 text-primary-emphasis">
                  About Rapid Lab & Diagnostics
                </h2>

                <p className="fw-semibold text-primary-emphasis">
                  Rapid Lab & Diagnostics is a modern, fully equipped diagnostic
                  center located in Tema, dedicated to providing high-quality
                  medical testing services that patients and healthcare
                  providers can depend on. We combine advanced laboratory
                  technology with skilled professionals to ensure that every
                  test is carried out with the highest level of accuracy and
                  reliability. Our facility offers a comprehensive range of
                  laboratory and diagnostic services designed to meet the needs
                  of individuals, clinics, and hospitals. From routine medical
                  tests to more specialized diagnostic investigations, we focus
                  on delivering fast and precise results that support timely and
                  informed medical decisions. At Rapid Lab & Diagnostics, we
                  understand that accurate results alone are not enough. That is
                  why we emphasize efficiency, ensuring that patients receive
                  their results as quickly as possible without compromising
                  quality. Our processes are streamlined to reduce waiting times
                  while maintaining strict quality control standards. Patient
                  care is central to our operations. We strive to create a
                  welcoming and comfortable environment where patients feel
                  respected and well cared for throughout their visit. Our staff
                  are trained to handle each patient with professionalism,
                  compassion, and confidentiality. We are committed to
                  maintaining the highest standards of accuracy,
                  professionalism, and integrity in all our services. By
                  continuously improving our systems and adopting modern
                  diagnostic technologies, we aim to remain a trusted partner in
                  healthcare delivery within Tema and beyond.
                  <br /> We provide a full range of laboratory and diagnostic
                  services with precision, speed, and care.
                </p>

                <p className="fw-semibold text-primary-emphasis">
                  Patient care, professionalism, and accuracy remain at the core
                  of everything we do.
                </p>

                {/* ACCREDITATION */}
                <div className="mt-3 p-3 bg-light rounded border text-primary-emphasis">
                  <h4 className="text-primary-emphasis mb-2">
                    Our Accreditations & Regulatory Compliance
                  </h4>
                  <ul className="mb-0 fw-semibold text-primary-emphasis">
                    <li>
                      Licensed by the{" "}
                      <strong>
                        Health Facilities Regulatory Agency (HeFRA)
                      </strong>{" "}
                      for safe and legal operation of medical laboratory
                      services.
                    </li>

                    <li>
                      Approved and monitored by the{" "}
                      <strong>Ghana Health Service (GHS)</strong> in line with
                      national healthcare delivery standards.
                    </li>

                    <li>
                      Committed to international quality standards through
                      alignment with{" "}
                      <strong>
                        Ghana National Accreditation Service (GhaNAS)
                      </strong>{" "}
                      frameworks.
                    </li>

                    <li>
                      <strong>NHIS Accredited Facility</strong> enabling
                      patients to access laboratory services using the National
                      Health Insurance Scheme.
                    </li>
                  </ul>
                </div>

                {/* MISSION / VISION CARDS */}
                <div className="row mt-4 g-3">
                  <div className="col-md-6">
                    <div className="card h-100 border-0 shadow-sm hover-card">
                      <div className="card-body">
                        <h5 className="text-primary-emphasis">Mission</h5>
                        <p className="small fw-semibold mb-0">
                          Committed to delivering fast, accurate, and reliable
                          diagnostics that strengthen healthcare delivery by
                          enabling precise clinical decision-making, supporting
                          early diagnosis, and improving the overall standard of
                          patient care.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card h-100 border-0 shadow-sm hover-card">
                      <div className="card-body">
                        <h5 className="text-primary-emphasis">Vision</h5>
                        <p className="small fw-semibold mb-0">
                          To be the most trusted diagnostic center in Ghana and
                          beyond.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CAREERS SECTION ================= */}
      <section
        id="careers"
        style={{ scrollMarginTop: "80px" }}
        className="py-5 bg-white border-top"
      >
        <div className="container text-center">
          <h2 className="text-primary-emphasis mb-3">Careers at Rapid Lab</h2>

          <div className="card border-0 shadow-sm p-4 col-lg-8 mx-auto">
            <p className="fw-semibold text-primary-emphasis">
              Join a team dedicated to excellence in healthcare diagnostics. We
              offer a supportive environment that encourages growth and
              innovation.
            </p>

            <h5 className="mt-3">Why Work With Us?</h5>
            <ul className="list-unstyled fw-semibold">
              <li>✔ Professional work environment</li>
              <li>✔ Career growth opportunities</li>
              <li>✔ Modern medical technology</li>
              <li>✔ Strong teamwork culture</li>
            </ul>

            <Link
              to="mailto:rapidlabdiagnostics@yahoo.com"
              className="btn btn-primary mt-3"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default About;
